import { useState } from 'react'
import { toast } from 'sonner'
import { useApplyVendorCredit } from '@/features/invoices/data/vendor-mutations'
import { useVendorCredits } from '@/features/invoices/queries'
import { type ApInvoiceDetail } from '@/features/invoices/view-models'
import { ActionPopover } from '@/components/action-popover'
import { SearchSelect } from '@/components/search-select'
import { Button } from '@/components/ui/button'
import { ComposeInput } from '@/components/compose-field'
import { Label } from '@/components/ui/label'
import { formatMoney } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

/** Credit the supplier owes us, and applying it to this invoice. Shown only when there is any. */
export function VendorCreditLine({
  invoice,
  invoiceId,
  canApply,
}: {
  invoice: ApInvoiceDetail
  invoiceId: string
  canApply: boolean
}) {
  const vendorCreditsQuery = useVendorCredits(invoice.vendor?.id)
  const applyVendorCredit = useApplyVendorCredit(invoice.vendor?.id)
  const [open, setOpen] = useState(false)
  const [memoUuid, setMemoUuid] = useState('')
  const [amount, setAmount] = useState('')

  const credits = vendorCreditsQuery.data?.items ?? []
  const available = credits.filter((memo) => {
    if (memo.derived || memo.direction !== 'credit_issued' || memo.status === 'void' || !memo.uuid || !memo.id)
      return false
    const applied = credits
      .filter((application) => application.source_credit_memo_id === memo.id && application.status !== 'void')
      .reduce((sum, application) => sum + Number(application.amount || 0), 0)
    return Number(memo.amount) - applied > 0.01
  })
  const appliedHere = (invoice.credit_memos ?? [])
    .filter((memo) => memo.direction === 'credit_applied' && memo.status === 'applied')
    .reduce((sum, memo) => sum + Number(memo.amount || 0), 0)
  const balance = Number(vendorCreditsQuery.data?.balance ?? 0)
  if (!available.length && appliedHere <= 0 && balance <= 0) return null

  const submit = (reason: string, close: () => void) => {
    const value = Number(amount)
    if (!memoUuid || !Number.isFinite(value) || value <= 0) {
      toast.error('Choose an issued credit and enter a positive amount')
      return
    }
    if (!reason) {
      toast.error('Explain why this credit is being applied')
      return
    }
    applyVendorCredit.mutate(
      {
        memoUuid,
        body: {
          invoice_uuid: invoiceId,
          amount,
          reason,
          idempotency_key: `${invoiceId}:${memoUuid}:${Date.now()}`,
        },
      },
      {
        onSuccess: () => {
          toast.success('Vendor credit applied as reconciliation evidence')
          close()
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Credit application failed')),
      },
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
      <dl className="flex flex-wrap gap-x-6 gap-y-1">
        <div className="flex gap-2">
          <dt className="text-muted-foreground">Supplier credit</dt>
          <dd className="font-medium tabular-nums">{formatMoney(balance)}</dd>
        </div>
        {appliedHere > 0 && (
          <div className="flex gap-2">
            <dt className="text-muted-foreground">Applied here</dt>
            <dd className="font-medium tabular-nums">{formatMoney(appliedHere)}</dd>
          </div>
        )}
      </dl>
      {available.length > 0 && (
        <ActionPopover
          align="end"
          open={open}
          onOpenChange={(next) => {
            if (next) {
              setMemoUuid(available[0]?.uuid || '')
              setAmount('')
            }
            setOpen(next)
          }}
          trigger={
            <Button size="sm" variant="outline" disabled={!canApply}>
              Apply credit
            </Button>
          }
          title="Apply vendor credit"
          note={{ label: 'Reason', placeholder: 'Reason', minLength: 1 }}
          confirmLabel="Apply credit evidence"
          pending={applyVendorCredit.isPending}
          pendingLabel="Applying..."
          onConfirm={submit}
        >
          <div className="space-y-1">
            <Label htmlFor="apply-credit-memo" className="text-xs">
              Issued credit
            </Label>
            <SearchSelect
              id="apply-credit-memo"
              className="h-8 w-full"
              value={memoUuid || undefined}
              options={available.map((memo) => ({
                value: memo.uuid || '',
                label: `${formatMoney(memo.amount)} • ${memo.memo_raw || 'Issued credit'}`,
              }))}
              onChange={(value) => value !== undefined && setMemoUuid(value)}
              placeholder="Choose a credit"
              searchPlaceholder="Search credits"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="credit-amount" className="text-xs">
              Amount to apply
            </Label>
            <ComposeInput
              id="credit-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </ActionPopover>
      )}
    </div>
  )
}
